import { Box, Button, FormHelperText, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { map, omit } from 'lodash';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FormikArray } from 'components/FormikArray';

export const CUSTOMERS_FORM_ERROR_KEYS = {
  DUPLICATE: 'customers.customerId',
  SHOP_CONFLICT: 'shop.customers.customerId',
} as const;

const ERROR_MESSAGES: Record<string, string> = {
  [CUSTOMERS_FORM_ERROR_KEYS.DUPLICATE]: 'errors.customers_duplicate',
  [CUSTOMERS_FORM_ERROR_KEYS.SHOP_CONFLICT]: 'errors.customer_exists_in_shop',
};

type Props = {
  name?: string;
  label: string;
  addItemLabel: string;
  defaultValue: any;
  ItemComponent: React.FC<{ index: number; item: any }>;
  importButtons?: {
    label: string;
    onClick: () => void;
  }[];
};

export const CustomersFormSection: React.FC<Props> = ({
  name = 'customers',
  label,
  addItemLabel,
  defaultValue,
  ItemComponent,
  importButtons,
}) => {
  const { t } = useTranslation();
  const { errors, setErrors, validateForm } = useFormikContext();

  const errorText = useMemo(() => {
    const key = Object.keys(ERROR_MESSAGES).find((k) => !!(errors as any)[k]);
    return key ? (t(ERROR_MESSAGES[key] as any) as string) : undefined;
  }, [errors, t]);

  const handleChange = useCallback(() => {
    setErrors(omit(errors as any, Object.keys(ERROR_MESSAGES)));
    validateForm();
  }, [errors, setErrors, validateForm]);

  return (
    <Fragment>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.5,
        }}
      >
        <Typography variant="subtitle1">{label}</Typography>
        <div>
          {map(importButtons, (btn) => (
            <Button
              key={btn.label}
              size="small"
              variant="outlined"
              sx={{ ml: 2 }}
              onClick={btn.onClick}
            >
              {btn.label}
            </Button>
          ))}
        </div>
      </Box>
      <FormikArray
        addItemLabel={addItemLabel}
        name={name}
        removable
        defaultValue={defaultValue}
        ItemComponent={ItemComponent}
        onChange={handleChange}
      />
      {errorText && <FormHelperText error>{errorText}</FormHelperText>}
    </Fragment>
  );
};
